"use client";

import { useState, useEffect, useCallback } from "react";
import Image from "next/image";
import { useTranslation } from "react-i18next";
import { useBookingModal } from "@/contexts/BookingModalContext";
import { motion, AnimatePresence } from "framer-motion";

// Add images to /public/new/ and extend this array to showcase more (e.g. /new/4.jpg)
const HERO_IMAGES = ["/new/1.jpg", "/new/2.jpg", "/new/3.jpg"] as const;
const AUTOPLAY_INTERVAL = 5500;

const HERO_ALT_KEYS = ["images.heroAlt1", "images.heroAlt2", "images.heroAlt3"] as const;

export default function HeroSection() {
  const { t } = useTranslation();
  const { openModal } = useBookingModal();
  const [index, setIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const goTo = useCallback(
    (idx: number) => {
      setIndex((idx + HERO_IMAGES.length) % HERO_IMAGES.length);
    },
    []
  );

  useEffect(() => {
    if (isPaused) return;
    const id = setInterval(() => {
      setIndex((prev) => (prev + 1) % HERO_IMAGES.length);
    }, AUTOPLAY_INTERVAL);
    return () => clearInterval(id);
  }, [isPaused]);

  return (
    <section
      className="relative isolate flex min-h-[60vh] flex-col justify-center overflow-hidden py-12 text-white sm:py-16 md:min-h-[70vh] md:py-24 lg:py-28"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Carousel images */}
      <div className="absolute inset-0">
        <AnimatePresence initial={false}>
          <motion.div
            key={index}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8, ease: "easeInOut" }}
            className="absolute inset-0"
            style={{ willChange: "opacity" }}
          >
            <Image
              src={HERO_IMAGES[index]}
              alt={t(HERO_ALT_KEYS[index])}
              fill
              priority={index === 0}
              fetchPriority={index === 0 ? "high" : undefined}
              sizes="100vw"
              className="object-cover object-center"
              quality={80}
              loading={index === 0 ? undefined : "lazy"}
            />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Black gradient overlay */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(to bottom, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.65) 100%)",
        }}
        aria-hidden
      />

      {/* Content */}
      <div className="relative z-10 mx-auto w-full max-w-6xl px-4 sm:px-6">
        <div className="max-w-2xl">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/80 sm:text-xs sm:tracking-[0.25em]">
            {t("hero.badge")}
          </p>
          <h1 className="mt-3 text-2xl font-black tracking-tight text-white sm:mt-4 sm:text-3xl md:text-4xl lg:text-5xl leading-tight">
            {t("hero.title")}
          </h1>
          <p className="mt-4 text-sm text-white/90 leading-relaxed max-w-xl sm:mt-6 sm:text-base">
            {t("hero.subtitle")}
          </p>
          <button
            type="button"
            onClick={openModal}
            className="mt-6 flex w-full min-h-[3rem] items-center justify-center rounded-xl bg-white px-6 py-4 text-center text-base font-semibold text-primary-strong shadow-lg transition active:scale-[0.98] hover:bg-white/95 sm:mt-8 sm:w-auto sm:min-w-[12rem] sm:rounded-lg sm:py-3.5 sm:text-sm md:px-8"
          >
            {t("hero.bookSlot")}
          </button>
        </div>
      </div>

      {/* Dot indicators */}
      <div className="absolute bottom-6 left-1/2 z-10 flex -translate-x-1/2 gap-2 sm:bottom-8">
        {HERO_IMAGES.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => goTo(i)}
            aria-label={`Go to slide ${i + 1}`}
            className="group flex h-2 w-2 items-center justify-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-white/60 focus:ring-offset-2 focus:ring-offset-transparent"
          >
            <span
              className={`block h-2 w-2 rounded-full transition-all duration-300 ${
                i === index
                  ? "w-6 bg-white"
                  : "bg-white/50 hover:bg-white/70"
              }`}
            />
          </button>
        ))}
      </div>
    </section>
  );
}
